// The target dimensions for visual signatures
const VISUAL_SIGNATURE_WIDTH = 300
const VISUAL_SIGNATURE_HEIGHT = 140

export const VISUAL_SIGNATURE_DETAILS_DEFAULTS: Omit<VisualSignatureDetails, 'id'> = {
  alias: '',
  name: '',
  optionalLine: '',
  optionalLine2: '',
  displayDate: true,
  displayClaim: true,
  displayStandards: true,
}

export const useVisualSignatureStore = defineStore('visualSignature', () => {
  const directSignStore = useDirectSignStore()
  const languageStore = useLanguageStore()

  const { $api } = useNuxtApp()

  const { getCurrentTimezone } = useDate()

  const { isEditDateFlag, isEditClaimFlag, isEditStandardsFlag } = useVisualSignatureFlags()

  const signatures = ref<VisualSignatureDetails[]>([])

  const defaultSignatureId = ref('')

  const selectedSignature = computed(() => {
    if (!defaultSignatureId.value) return signatures.value[0]

    return signatures.value.find(signature => signature.id === defaultSignatureId.value)
  })

  const getEffectiveSettings = async () => {
    return await $api('/api/users/me/visual-signatures/settings/effective')
  }

  const updateSignatures = async () => {
    const effectiveSettings = await getEffectiveSettings()

    const { defaultSignature, signatures: signatureDetails } = await $api('/api/users/me/visual-signatures')

    // Settings the user can no longer edit (e.g. after a plan downgrade) are
    // forced to the effective values, ignoring stale per-signature values in
    // the database. Editable settings keep the signature's own stored value.
    const enforcedSettings = {
      ...(!isEditDateFlag.value && { displayDate: effectiveSettings.displayDate }),
      ...(!isEditClaimFlag.value && { displayClaim: effectiveSettings.displayClaim }),
      ...(!isEditStandardsFlag.value && { displayStandards: effectiveSettings.displayStandards }),
    }

    signatures.value = signatureDetails.map(details => ({
      ...VISUAL_SIGNATURE_DETAILS_DEFAULTS,
      ...stripNullishValues(details),
      ...enforcedSettings,
    }))
    defaultSignatureId.value = defaultSignature
  }

  const createSignature = async (details: Omit<VisualSignatureDetails, 'id'> & { customImage?: CustomImageData }) => {
    await $api('/api/users/me/visual-signatures', {
      method: 'POST',
      body: details,
    })
    await updateSignatures()
  }

  const updateSignature = async (id: string, updates: Partial<Omit<VisualSignatureDetails, 'id'>>) => {
    await $api(`/api/users/me/visual-signatures/${id}`, {
      method: 'PUT',
      body: updates,
    })
    await updateSignatures()
  }

  const deleteSignature = async (id: string) => {
    await $api(`/api/users/me/visual-signatures/${id}`, {
      method: 'DELETE',
    })
    await updateSignatures()
  }

  const getSignatureImage = async (signatureId: string) => {
    return await $api(`/api/users/me/visual-signatures/${signatureId}/image-data`)
  }

  const removeSignatureImage = async (signatureId: string) => {
    await $api(`/api/users/me/visual-signatures/${signatureId}/image-data`, {
      method: 'DELETE',
    })
  }

  const saveSignatureImage = async (signatureId: string, imageData: CustomImageData) => {
    await $api(`/api/users/me/visual-signatures/${signatureId}/image-data`, {
      method: 'PUT',
      body: imageData,
    })
  }

  /**
   * Fetches a rendered visual signature image for a predefined signature.
   *
   * @param signatureId The ID of the predefined visual signature
   * @param parameters The visual signature metadata
   */
  const getPredefinedVisualSignature = async (signatureId: string, parameters: VisualSignatureData) => {
    return await $api<Blob>(`/api/users/me/visual-signatures/${signatureId}/render`, {
      method: 'POST',
      body: {
        language: languageStore.language,
        timezone: getCurrentTimezone(),
        ...parameters,
      },
      responseType: 'blob',
    })
  }

  /**
   * Fetches a visual signature image matching the provided parameters.
   *
   * @param parameters The visual signature metadata
   * @param image The optional custom image to be rendered on the visual signature
   */
  const getVisualSignature = async (parameters: VisualSignatureData, image?: CustomImageData | null) => {
    if (directSignStore.isDirectSign) {
      return await $api<Blob>('/api/visual-signatures/image', {
        method: 'POST',
        body: parameters,
        responseType: 'blob',
      })
    } else {
      return await $api<Blob>('api/visual-signatures/image', {
        method: 'POST',
        body: {
          ...parameters,
          ...(Boolean(image) && { image }),
        },
        responseType: 'blob',
      })
    }
  }

  /**
   * Fetches a visual signature placeholder image.
   *
   * @param parameters The visual signature metadata
   * @param image The optional custom image to be rendered instead of name
   */
  const getVisualSignaturePlaceholder = async (parameters: VisualSignatureData, image?: CustomImageData | null) => {
    const { name, signerGroup, quality, legislation, language } = parameters

    return await $api<Blob>('/api/visual-signatures/placeholder', {
      method: 'POST',
      body: signerGroup
        ? {
            type: 'group',
            name: signerGroup.name,
            quality,
            legislation,
            language,
          }
        : {
            type: 'default',
            name,
            quality,
            legislation,
            language,
            ...(Boolean(image) && { image }),
          },
      responseType: 'blob',
    })
  }

  const getSealImage = async (sealName: string) => {
    const businessStore = useBusinessStore()
    return await businessStore.getSealImage(sealName)
  }

  const sealDimensions = ref<Map<string, { width: number; height: number }>>(new Map())

  /**
   * Fits the seal dimensions to the target dimensions.
   *
   * We need to handle 'pre-placed' seals when users switch their signing method from 'signature' to 'seal'. As we only
   * know the placement data for a usually differently sized visual signature, we need to fit the seal to the target.
   *
   * @param sealId
   * @param targetDimensions
   */
  const fitSealDimensions = (sealId: string, targetDimensions: { width: number; height: number }) => {
    const dimensions = sealDimensions.value.get(sealId)

    if (!dimensions) return targetDimensions

    const widthRatio = targetDimensions.width / dimensions.width
    const heightRatio = targetDimensions.height / dimensions.height

    const ratio = Math.min(widthRatio, heightRatio)

    return {
      width: dimensions.width * ratio,
      height: dimensions.height * ratio,
    }
  }

  return {
    signatures,
    defaultSignatureId,
    selectedSignature,
    sealDimensions,
    defaultSignatureDimensions: { width: VISUAL_SIGNATURE_WIDTH, height: VISUAL_SIGNATURE_HEIGHT },
    getEffectiveSettings,
    createSignature,
    updateSignature,
    updateSignatures,
    deleteSignature,
    getSignatureImage,
    removeSignatureImage,
    saveSignatureImage,
    getPredefinedVisualSignature,
    getVisualSignaturePlaceholder,
    getVisualSignature,
    getSealImage,
    fitSealDimensions,
  }
})
